import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { SearchContext } from '../Contexts';
import { useFetch } from '../hooks/useFetch';
import Results from './Results';
import FacetLists from './FacetLists';
import DateRange from './DateRange';
import CurrentSelection from './CurrentSelection';
import ClearButton from './ClearButton';
import QueryStringParams from './QueryStringParams';

const AdvancedSearch = ({
  advancedIndex,
  query,
  setQuery,
  mainSearchData,
  selectedTab,
  setSelectedTab,
  setTabs,
  tabs,
  tabsData,
  isModal,
  cleanQueryString,
  searchId,
  isTabsLoaded
}) => {
  const root = mainSearchData[advancedIndex];
  const [facetsLists, setFacetsLists] = useState(root.facetsListData);
  const [selectedFacets, setSelectedFacets] = useState({});
  const [dateRange, setDateRange] = useState([
    { label: 'From', value: '', placeholder: 'Year' },
    { label: 'To', value: '', placeholder: 'Year' }
  ]);
  const [dropdownValue, setDropdownValue] = useState(['Year', 'Year']);
  const [expandedOptions, setExpandedOptions] = useState(false);
  const [activeSearch, setActiveSearch] = useState('');
  const years = root.dateRange;
  const isLoaded = useRef(false);

  const { results, loading, currentKeys } = useFetch(root.endpoint, {
    query,
    selectedFacets,
    facetsLists,
    dateRange,
    selectedTab,
    setSelectedTab,
    tabs,
    cleanQueryString,
    isLoaded
  });

  const handleAvailableTabs = () => {
    if (!results.tabsToShow) return;

    const commonValues = tabsData.filter(tab =>
      results.tabsToShow.includes(tab.value)
    );

    if (commonValues.length) setTabs(commonValues);
  };

  const dateRangeExists =
    dateRange && dateRange.length && (dateRange[0].value || dateRange[1].value);

  const check = Boolean(
    (query && query.length >= 3) || currentKeys.length || dateRangeExists
  );

  const tabSelectedLink = document.querySelector('.tab-button.is-active a');
  const mainContent = document.querySelector(
    `#${tabSelectedLink?.dataset?.panelId} .main-content`
  );

  useEffect(() => {
    if (results?.resultsList && results?.tabsToShow.length) {
      handleAvailableTabs();
    }
    if (check && mainContent) {
      mainContent.style.display = 'none';
    } else if (!check && mainContent) {
      mainContent.removeAttribute('style');
    }
  }, [query, selectedFacets, dateRange, results, check]);

  useEffect(() => {
    if (!isLoaded.current) {
      isLoaded.current = true;
    }
  }, []);

  const checkActiveSearch = e => {
    cleanQueryString();
    const { target } = e;
    let mainTarget;

    if (target.closest('[data-panel-id]')) {
      mainTarget = target.closest('[data-panel-id]');
    } else if (target.closest('.modal-close')) {
      mainTarget = target.closest('.modal-close');
    } else {
      mainTarget = target;
    }

    const modalSearch = document.querySelector(
      `.search-modal [data-search-id=${searchId}]`
    );
    const modalContainer = document.querySelector('.search-modal');

    let panelId;
    let currentTabSearch;
    if (mainTarget) {
      panelId = mainTarget.dataset?.panelId;
      currentTabSearch = document.querySelector(
        `#${panelId} [data-search-id=${searchId}]`
      );
    }

    if (
      !modalContainer.classList.contains('hide') &&
      !mainTarget.classList.contains('modal-close')
    ) {
      setActiveSearch(modalSearch);
    } else if (mainTarget.classList.contains('modal-close')) {
      const currentTab = document.querySelector('.tab-button.is-active a');
      if (currentTab) {
        panelId = currentTab.dataset?.panelId;
        currentTabSearch = document.querySelector(
          `#${panelId} [data-search-id=${searchId}]`
        );
      }

      setActiveSearch(currentTabSearch);
    } else {
      setActiveSearch(currentTabSearch);
    }
  };

  useEffect(() => {
    const onLoadActiveTab = document.querySelector('.tab-button.is-active a');
    let onLoadActiveSearch;

    if (onLoadActiveTab) {
      onLoadActiveSearch = document.querySelector(
        `#${onLoadActiveTab.dataset.panelId} [data-search-id=${searchId}] `
      );
    }

    const searchTriggers = document.querySelectorAll(
      `.tab-button,[data-open-modal="search"], .search-modal .modal-close`
    );

    if (searchTriggers.length) {
      searchTriggers.forEach(trigger => {
        trigger.addEventListener('click', checkActiveSearch);
      });
    }

    if (onLoadActiveTab) setActiveSearch(onLoadActiveSearch);

    return () => {
      if (searchTriggers.length) {
        searchTriggers.forEach(trigger => {
          trigger.removeEventListener('click', checkActiveSearch);
        });
      }
    };
  }, []);

  return (
    <SearchContext.Provider
      value={{
        query,
        setQuery,
        facetsLists,
        setFacetsLists,
        selectedFacets,
        setSelectedFacets,
        dateRange,
        setDateRange,
        dropdownValue,
        setDropdownValue,
        years,
        selectedTab,
        setSelectedTab,
        tabs,
        setExpandedOptions
      }}
    >
      {((activeSearch && activeSearch.dataset.searchId === searchId) ||
        isModal) && (
        <QueryStringParams
          isLoaded={isLoaded.current}
          isTabsLoaded={isTabsLoaded.current}
          searchId={searchId}
          cleanQueryString={cleanQueryString}
        />
      )}

      {facetsLists && facetsLists.length ? (
        <div
          className="advanced-search__facets"
          aria-expanded={expandedOptions}
        >
          <label
            className="advanced-search__facets-label"
            onClick={() => setExpandedOptions(!expandedOptions)}
          >
            {root.advancedSearchLabel}
          </label>
          <div className="advanced-search__facets-panel">
            {/* dropdown facets */}
            <FacetLists />
            {/* date range */}
            {years.length ? (
              <DateRange dateRangeLabel={root.dateRangeLabel} />
            ) : (
              ''
            )}
            {/* selected categories */}

            <CurrentSelection />

            {/* clear state button */}
            <ClearButton label={root.clearButtonLabel} />
          </div>
        </div>
      ) : (
        ''
      )}
      {/* result items */}
      <div className="advanced-search__results-container">
        {results?.resultsList?.length && !loading && check ? (
          <Results results={results?.resultsList} isModal={isModal} />
        ) : (
          ''
        )}
        {results &&
          !results?.resultsList?.length &&
          !loading &&
          query &&
          query.length >= 3 &&
          results.noResultsMessage && (
            <div className="advanced-search__results-item advanced-search__no-results-message">
              <h4 className="advanced-search__results-item-title">
                {results.noResultsMessage.title &&
                  results.noResultsMessage.title}
              </h4>
              <p className="advanced-search__results-details">
                {results.noResultsMessage.excerpt &&
                  results.noResultsMessage.excerpt}
              </p>
            </div>
          )}
      </div>
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      ) : (
        ''
      )}
    </SearchContext.Provider>
  );
};

AdvancedSearch.propTypes = {
  advancedIndex: PropTypes.number.isRequired,
  query: PropTypes.string,
  setQuery: PropTypes.func.isRequired,
  mainSearchData: PropTypes.array.isRequired,
  selectedTab: PropTypes.object.isRequired,
  setSelectedTab: PropTypes.func.isRequired,
  setTabs: PropTypes.func.isRequired,
  tabs: PropTypes.array.isRequired,
  tabsData: PropTypes.array.isRequired,
  isModal: PropTypes.string,
  searchId: PropTypes.string.isRequired,
  cleanQueryString: PropTypes.func.isRequired,
  isTabsLoaded: PropTypes.object.isRequired
};

export default AdvancedSearch;
